import React, { useState } from 'react';
import { Plus, FileText, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { ReportBuilderWizard } from '../components/reports/ReportBuilderWizard';
import { ReportsTable } from '../components/reports/ReportsTable';
import { ScheduledReportsTable } from '../components/reports/ScheduledReportsTable';
import { ScheduleModal } from '../components/reports/ScheduleModal';
import { useReports, useScheduledReports } from '../hooks/useReportsData';
import { GeneratedReport, ScheduledReport } from '../types/reports.types';

export const Reports: React.FC = () => {
  const [isBuilderOpen, setIsBuilderOpen] = useState(false);
  const [isScheduleOpen, setIsScheduleOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 10;
  
  const { data: reportsData, isLoading: reportsLoading } = useReports(currentPage, perPage);
  const { data: scheduledReports, isLoading: scheduledLoading } = useScheduledReports();
  
  const reports = (reportsData as any)?.reports || [];
  const totalReports = (reportsData as any)?.total || 0;
  const totalPages = Math.ceil(totalReports / perPage);
  
  const stats = [
    {
      icon: FileText,
      label: 'Total Reports',
      value: totalReports,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    },
    {
      icon: CheckCircle,
      label: 'Completed Today',
      value: reports.filter((r: GeneratedReport) => r.status === 'completed').length,
      color: 'text-green-600',
      bgColor: 'bg-green-100 dark:bg-green-900/30',
    },
    {
      icon: Clock,
      label: 'Scheduled Active',
      value: scheduledReports?.filter((r: ScheduledReport) => r.isActive).length || 0,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100 dark:bg-purple-900/30',
    },
    {
      icon: AlertCircle,
      label: 'Failed Last 24h',
      value: reports.filter((r: GeneratedReport) => r.status === 'failed').length,
      color: 'text-red-600',
      bgColor: 'bg-red-100 dark:bg-red-900/30',
    },
  ];
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Reports</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Generate and schedule custom reports</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setIsScheduleOpen(true)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <Clock className="w-4 h-4" />
            Schedule Report
          </button>
          <button
            onClick={() => setIsBuilderOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white hover:bg-blue-600 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Create Report
          </button>
        </div>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div
              key={index}
              className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{stat.label}</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stat.value}</p>
                </div>
                <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                  <Icon className={`w-6 h-6 ${stat.color}`} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Generated Reports */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Generated Reports</h2>
        </div>
        <ReportsTable reports={reports} isLoading={reportsLoading} />
        
        {totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Showing {(currentPage - 1) * perPage + 1} to {Math.min(currentPage * perPage, totalReports)} of {totalReports} reports
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Scheduled Reports */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Scheduled Reports</h2>
        </div>
        <ScheduledReportsTable reports={scheduledReports || []} isLoading={scheduledLoading} />
      </div>
      
      {/* Modals */}
      <ReportBuilderWizard isOpen={isBuilderOpen} onClose={() => setIsBuilderOpen(false)} />
      <ScheduleModal isOpen={isScheduleOpen} onClose={() => setIsScheduleOpen(false)} />
    </div>
  );
};

export default Reports;